import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit2, Trash2, Plus, Filter, CheckSquare, Star } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import DateRangePicker from '../../../src/components/DateRangePicker';
import DatePicker from '../../../src/components/DatePicker';
import { ORIGENS_PARA_FILTRO } from '../../../src/lib/origensNegociacao';

interface FilterState {
    status: string[];
    nome: string;
    qualificacao: number | null;
    consumoMin: string;
    consumoMax: string;
    dataCriacaoInicio: string;
    dataCriacaoFim: string;
    dataInteracao: string;
    dataTarefa: string;
    origem: string[];
    plataforma: string[];
}

/** 'ig'/'fb' vêm abreviados do lead automático do Meta Ads; o cadastro manual
 *  grava por extenso ("Instagram"/"Facebook"). Aqui os dois casam na mesma opção. */
const normalizePlataforma = (raw?: string | null): string => {
    const v = (raw || '').trim().toLowerCase();
    if (v === 'ig' || v === 'instagram') return 'Instagram';
    if (v === 'fb' || v === 'facebook') return 'Facebook';
    if (!v) return 'Não informado';
    return (raw as string).trim();
};

interface CRMFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (filters: FilterState) => void;
    currentFilters: FilterState;
}

const CRMFilterModal: React.FC<CRMFilterModalProps> = ({ isOpen, onClose, onApply, currentFilters }) => {
    const [filters, setFilters] = useState<FilterState>(currentFilters || {
        status: [], nome: '', qualificacao: null, consumoMin: '', consumoMax: '',
        dataCriacaoInicio: '', dataCriacaoFim: '', dataInteracao: '', dataTarefa: '', origem: [], plataforma: []
    });

    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showOrigemDropdown, setShowOrigemDropdown] = useState(false);
    const [showPlataformaDropdown, setShowPlataformaDropdown] = useState(false);

    const statusOptions = [
        { value: 'em_andamento', label: "Em andamento" },
        { value: 'simulacao_realizada', label: "Simulação realizada" },
        { value: 'termo_enviado', label: "Termo enviado" },
        { value: 'termo_assinado', label: "Termo assinado" },
        { value: 'termo_analise', label: "Termo em análise" },
        { value: 'termo_aprovado', label: "Termo aprovado" },
        { value: 'termo_reprovado', label: "Termo reprovado" },
        { value: 'perdido', label: "Perdido" },
        { value: 'em_pausa', label: "Em pausa" }
    ];

    const [origemOptions, setOrigemOptions] = useState<string[]>(ORIGENS_PARA_FILTRO);

    const [plataformaOptions, setPlataformaOptions] = useState<string[]>(["Instagram", "Facebook", "Não informado"]);

    useEffect(() => {
        if (currentFilters) {
            setFilters(currentFilters);
        }
    }, [currentFilters, isOpen]);

    useEffect(() => {
        const fetchOrigins = async () => {
            try {
                const { data } = await supabase
                    .from('negociacoes')
                    .select('origem');

                if (data) {
                    const uniqueOrigins = Array.from(new Set(data.map(n => n.origem).filter(Boolean)));
                    const defaults = ORIGENS_PARA_FILTRO;
                    const combined = Array.from(new Set([...defaults, ...uniqueOrigins])).sort();
                    setOrigemOptions(combined);
                }
            } catch (err) {
                console.error("Error fetching origins:", err);
            }
        };
        fetchOrigins();
    }, []);

    useEffect(() => {
        const fetchPlatforms = async () => {
            try {
                const { data } = await supabase
                    .from('negociacoes')
                    .select('plataforma');

                if (data) {
                    // Agrupa pela forma normalizada (Instagram/Facebook casam 'ig'/'fb' e as
                    // variações por extenso); o que sobrar entra como opção "crua" extra.
                    const outros = new Set<string>();
                    data.forEach(n => {
                        const norm = normalizePlataforma(n.plataforma);
                        if (norm !== 'Instagram' && norm !== 'Facebook' && norm !== 'Não informado') outros.add(norm);
                    });
                    setPlataformaOptions(['Instagram', 'Facebook', ...Array.from(outros).sort(), 'Não informado']);
                }
            } catch (err) {
                console.error("Error fetching platforms:", err);
            }
        };
        fetchPlatforms();
    }, []);

    const handleClear = () => {
        const emptyState: FilterState = {
            status: [], nome: '', qualificacao: null, consumoMin: '', consumoMax: '',
            dataCriacaoInicio: '', dataCriacaoFim: '', dataInteracao: '', dataTarefa: '', origem: [], plataforma: []
        };
        setFilters(emptyState);
    };

    const handleApply = () => {
        onApply(filters);
        onClose();
    };

    const toggleOrigem = (value: string) => {
        const currentOrigens = filters.origem || [];

        if (value === 'Todas') {
            setFilters({ ...filters, origem: [] });
            return;
        }

        let newOrigens = [...currentOrigens];
        if (newOrigens.includes(value)) {
            newOrigens = newOrigens.filter(o => o !== value);
        } else {
            newOrigens.push(value);
        }
        setFilters({ ...filters, origem: newOrigens });
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-[100] p-0 sm:p-4 md:p-8 animate-in fade-in duration-200">

            <div className="bg-white dark:bg-gray-800 w-full sm:max-w-3xl lg:max-w-4xl h-[95dvh] sm:h-auto sm:max-h-full shadow-2xl flex flex-col rounded-t-2xl sm:rounded-xl animate-in slide-in-from-bottom sm:zoom-in-95 duration-200">

                {/* Header (Fixo no topo) */}
                <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Filtros</h2>
                        <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs font-bold">
                            {filters && Object.entries(filters).filter(([k, v]) => {
                                if (Array.isArray(v)) return v.length > 0;
                                return v && v !== 'all';
                            }).length}
                        </span>
                    </div>
                    <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Body - Scrollable */}
                {/* CORREÇÃO 3: Adição do `min-h-0` aqui é essencial para a barra de rolagem funcionar dentro do flex-col */}
                <div className="flex-1 min-h-0 overflow-y-auto p-4 lg:p-6 space-y-5 lg:space-y-6 custom-scrollbar">

                    {/* Omitted Saved Filters Mock per user request */}

                    {/* Columns Layout */}
                    {/* CORREÇÃO 4: As 2 colunas agora ativam no 'md' ao invés do 'lg' para não espremer em notebooks */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">

                        {/* Status (Multi-select) */}
                        <div className="space-y-1.5 relative">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Status da negociação</label>

                            <button
                                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                className={`w-full flex items-center justify-between pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded-lg text-sm focus:outline-none transition-all
                                    ${showStatusDropdown ? 'ring-2 ring-primary/20 border-primary' : 'hover:border-gray-400 dark:hover:border-gray-500'}`}
                            >
                                <div className="flex items-center gap-2 truncate">
                                    <CheckSquare size={16} className="text-gray-400 shrink-0" />
                                    <span className="truncate text-gray-600 dark:text-gray-300">
                                        {(filters.status || []).length === 0 ? 'Todos os status' :
                                            (filters.status || []).length === 1 ? statusOptions.find(o => o.value === filters.status[0])?.label :
                                                `${filters.status.length} status selecionados`}
                                    </span>
                                </div>
                                <span className={`text-gray-400 transition-transform duration-300 ${showStatusDropdown ? 'rotate-180' : ''}`}>
                                    ▼
                                </span>
                            </button>

                            {/* Status Dropdown Menu */}
                            {showStatusDropdown && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowStatusDropdown(false)}></div>
                                    <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-2 z-50 animate-fade-in origin-top">
                                        <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-1">
                                            <label className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${(filters.status || []).length === 0 ? 'bg-gray-50 dark:bg-gray-700/50' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={(filters.status || []).length === 0}
                                                    onChange={() => setFilters({ ...filters, status: [] })}
                                                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Todos os status</span>
                                            </label>

                                            <div className="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2"></div>

                                            {statusOptions.map(opt => (
                                                <label key={opt.value} className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={(filters.status || []).includes(opt.value)}
                                                        onChange={(e) => {
                                                            const currentStatus = filters.status || [];
                                                            if (e.target.checked) {
                                                                setFilters({ ...filters, status: [...currentStatus, opt.value] });
                                                            } else {
                                                                setFilters({ ...filters, status: currentStatus.filter(s => s !== opt.value) });
                                                            }
                                                        }}
                                                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-primary focus:ring-primary"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">{opt.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Nome */}
                        <div className="space-y-1.5">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Nome da Negociação</label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none dark:text-gray-200"
                                value={filters.nome}
                                onChange={(e) => setFilters({ ...filters, nome: e.target.value })}
                            />
                        </div>

                        {/* Origem (Multi-select Dropdown) */}
                        <div className="space-y-1.5 relative col-span-1">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Origem</label>

                            <button
                                onClick={() => setShowOrigemDropdown(!showOrigemDropdown)}
                                className={`w-full flex items-center justify-between pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded-lg text-sm focus:outline-none transition-all
                                    ${showOrigemDropdown ? 'ring-2 ring-primary/20 border-primary' : 'hover:border-gray-400 dark:hover:border-gray-500'}`}
                            >
                                <div className="flex items-center gap-2 truncate">
                                    <CheckSquare size={16} className="text-gray-400 shrink-0" />
                                    <span className="truncate text-gray-600 dark:text-gray-300">
                                        {(filters.origem || []).length === 0 ? 'Todas as origens' :
                                            (filters.origem || []).length === 1 ? filters.origem[0] :
                                                `${filters.origem.length} origens selecionadas`}
                                    </span>
                                </div>
                                <span className={`text-gray-400 transition-transform duration-300 ${showOrigemDropdown ? 'rotate-180' : ''}`}>
                                    ▼
                                </span>
                            </button>

                            {/* Origem Dropdown Menu */}
                            {showOrigemDropdown && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowOrigemDropdown(false)}></div>
                                    <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-2 z-50 animate-fade-in origin-top">
                                        <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-1">
                                            <label className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${(filters.origem || []).length === 0 ? 'bg-gray-50 dark:bg-gray-700/50' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={(filters.origem || []).length === 0}
                                                    onChange={() => setFilters({ ...filters, origem: [] })}
                                                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-primary focus:ring-primary"
                                                />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Todas as origens</span>
                                            </label>
                                            <div className="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2"></div>

                                            {origemOptions.map(opt => (
                                                <label key={opt} className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={(filters.origem || []).includes(opt)}
                                                        onChange={(e) => {
                                                            const currentOrigens = filters.origem || [];
                                                            if (e.target.checked) {
                                                                setFilters({ ...filters, origem: [...currentOrigens, opt] });
                                                            } else {
                                                                setFilters({ ...filters, origem: currentOrigens.filter(o => o !== opt) });
                                                            }
                                                        }}
                                                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-primary focus:ring-primary"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">{opt}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Plataforma / Rede social (Multi-select Dropdown) */}
                        <div className="space-y-1.5 relative col-span-1">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300 block">Plataforma</label>

                            <button
                                onClick={() => setShowPlataformaDropdown(!showPlataformaDropdown)}
                                className={`w-full flex items-center justify-between pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded-lg text-sm focus:outline-none transition-all
                                    ${showPlataformaDropdown ? 'ring-2 ring-primary/20 border-primary' : 'hover:border-gray-400 dark:hover:border-gray-500'}`}
                            >
                                <div className="flex items-center gap-2 truncate">
                                    <CheckSquare size={16} className="text-gray-400 shrink-0" />
                                    <span className="truncate text-gray-600 dark:text-gray-300">
                                        {(filters.plataforma || []).length === 0 ? 'Todas as plataformas' :
                                            (filters.plataforma || []).length === 1 ? filters.plataforma[0] :
                                                `${filters.plataforma.length} plataformas selecionadas`}
                                    </span>
                                </div>
                                <span className={`text-gray-400 transition-transform duration-300 ${showPlataformaDropdown ? 'rotate-180' : ''}`}>
                                    ▼
                                </span>
                            </button>

                            {/* Plataforma Dropdown Menu */}
                            {showPlataformaDropdown && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowPlataformaDropdown(false)}></div>
                                    <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-2 z-50 animate-fade-in origin-top">
                                        <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-1">
                                            <label className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${(filters.plataforma || []).length === 0 ? 'bg-gray-50 dark:bg-gray-700/50' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={(filters.plataforma || []).length === 0}
                                                    onChange={() => setFilters({ ...filters, plataforma: [] })}
                                                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-primary focus:ring-primary"
                                                />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Todas as plataformas</span>
                                            </label>
                                            <div className="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2"></div>

                                            {plataformaOptions.map(opt => (
                                                <label key={opt} className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={(filters.plataforma || []).includes(opt)}
                                                        onChange={(e) => {
                                                            const currentPlataformas = filters.plataforma || [];
                                                            if (e.target.checked) {
                                                                setFilters({ ...filters, plataforma: [...currentPlataformas, opt] });
                                                            } else {
                                                                setFilters({ ...filters, plataforma: currentPlataformas.filter(o => o !== opt) });
                                                            }
                                                        }}
                                                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-primary focus:ring-primary"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">{opt}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Qualificação (Stars) */}
                        <div className="space-y-1.5">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Qualificação</label>
                            <div className="flex items-center gap-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 h-[38px]">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        onClick={() => setFilters({ ...filters, qualificacao: star === filters.qualificacao ? null : star })}
                                        className="focus:outline-none transition-transform hover:scale-110"
                                    >
                                        <Star
                                            size={20}
                                            className={`${(filters.qualificacao || 0) >= star ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
                                        />
                                    </button>
                                ))}
                                <span className="text-xs text-gray-400 ml-2">
                                    {(filters.qualificacao || 0) > 0 ? `${filters.qualificacao} estrela(s)` : 'Selecionar'}
                                </span>
                            </div>
                        </div>

                        {/* Consumo */}
                        <div className="space-y-1.5">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Consumo médio (kWh)</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none dark:text-gray-200"
                                    placeholder="Min"
                                    value={filters.consumoMin}
                                    onChange={(e) => setFilters({ ...filters, consumoMin: e.target.value })}
                                />
                                <span className="text-sm text-gray-500">até</span>
                                <input
                                    type="number"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none dark:text-gray-200"
                                    placeholder="Max"
                                    value={filters.consumoMax}
                                    onChange={(e) => setFilters({ ...filters, consumoMax: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Datas (Criação / Interação / Tarefa) — mesma linha */}
                        <div className="space-y-1.5 col-span-1 md:col-span-2">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
                                {/* Data Criação (Range) */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Data de criação (Período)</label>
                                    <DateRangePicker
                                        startDate={filters.dataCriacaoInicio}
                                        endDate={filters.dataCriacaoFim}
                                        onChange={(s, e) => setFilters({ ...filters, dataCriacaoInicio: s, dataCriacaoFim: e })}
                                    />
                                </div>

                                {/* Data Interação */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Data da última interação</label>
                                    <DatePicker
                                        value={filters.dataInteracao}
                                        onChange={(d) => setFilters({ ...filters, dataInteracao: d })}
                                    />
                                </div>

                                {/* Data Tarefa */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Data da próxima tarefa</label>
                                    <DatePicker
                                        value={filters.dataTarefa}
                                        onChange={(d) => setFilters({ ...filters, dataTarefa: d })}
                                        align="right"
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Footer (Fixo no rodapé do modal) */}
                <div className="p-4 lg:p-6 border-t border-gray-100 dark:border-gray-700 flex flex-col-reverse md:flex-row gap-3 lg:gap-4 bg-gray-50/50 dark:bg-gray-900/50 shrink-0">
                    <button
                        onClick={handleClear}
                        className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-bold text-sm hover:bg-white dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-all"
                    >
                        Limpar filtros
                    </button>
                    <button
                        onClick={handleApply}
                        className="flex-1 py-2.5 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                    >
                        Aplicar filtros
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CRMFilterModal;